import { Button as ButtonPrimitive } from '@base-ui/react'
import type { LucideIcon } from 'lucide-react'
import type { ButtonHTMLAttributes } from 'react'

import { cn } from '@/lib/cn'

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: LucideIcon
  /** Required: an icon-only control must still name itself to a screen reader. */
  label: string
  size?: 'sm' | 'md'
}

/** Icon-only control. Never use this for a destructive or financial action —
 *  those always carry a text label (DESIGN.md § Icons). */
export function IconButton({
  icon: Icon,
  label,
  size = 'md',
  className,
  type = 'button',
  ...props
}: IconButtonProps) {
  return (
    <ButtonPrimitive
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-md border border-transparent',
        'text-text-muted cursor-pointer transition-[background-color,color]',
        'duration-[var(--dur-fast)] ease-[var(--ease-standard)]',
        'hover:bg-surface-hover hover:text-text-heading',
        'disabled:cursor-not-allowed disabled:opacity-55',
        // Desktop measure; a coarse pointer grows both to the 44px touch
        // minimum (UX-DR-27). An icon button is the smallest target in the
        // system and therefore the one that misses most on a phone.
        size === 'sm' ? 'size-7 pointer-coarse:size-11' : 'size-8 pointer-coarse:size-11',
        className,
      )}
      {...props}
    >
      <Icon size={size === 'sm' ? 15 : 17} aria-hidden />
    </ButtonPrimitive>
  )
}
